import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, DollarSign, Briefcase, CheckCircle2 } from "lucide-react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";

export default function JobDetail() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Job not found"));
    }, [id]);

    if (err) {
        return (
            <PageShell>
                <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                    <div className="font-heading text-3xl font-bold text-brand-navy">{err}</div>
                    <Link to="/jobs" className="mt-4 inline-block text-brand-orange font-bold">Back to jobs</Link>
                </div>
            </PageShell>
        );
    }
    if (!job) return <PageShell><div className="max-w-3xl mx-auto p-12 text-slate-500">Loading...</div></PageShell>;

    return (
        <PageShell>
            <section className="bg-brand-navy text-white py-16">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <Link to="/jobs" className="inline-flex items-center gap-2 text-slate-300 hover:text-brand-orange text-sm font-bold mb-6" data-testid="back-to-jobs-link">
                        <ArrowLeft className="h-4 w-4" /> All jobs
                    </Link>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-bold">{job.category}</div>
                    <h1 className="mt-3 font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95]">{job.title}</h1>
                    <div className="mt-6 flex flex-wrap gap-6 text-slate-200 text-sm">
                        <span className="flex gap-2"><MapPin className="h-4 w-4 text-brand-orange" />{job.location}</span>
                        <span className="flex gap-2"><Clock className="h-4 w-4 text-brand-orange" />{job.shift}</span>
                        <span className="flex gap-2"><Briefcase className="h-4 w-4 text-brand-orange" />{job.employment_type}</span>
                        <span className="flex gap-2 font-bold text-white"><DollarSign className="h-4 w-4 text-brand-orange" />{job.pay_rate}</span>
                    </div>
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 grid lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2">
                    <h2 className="font-heading text-2xl font-black uppercase text-brand-navy">About this role</h2>
                    <p className="mt-4 text-slate-700 leading-relaxed">{job.description}</p>

                    {job.requirements?.length > 0 && (
                        <>
                            <h3 className="mt-10 font-heading text-xl font-black uppercase text-brand-navy">Requirements</h3>
                            <ul className="mt-4 space-y-3">
                                {job.requirements.map((r, i) => (
                                    <li key={i} className="flex gap-3 text-slate-700">
                                        <CheckCircle2 className="h-5 w-5 text-brand-orange shrink-0 mt-0.5" />{r}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                <aside className="lg:col-span-1">
                    <div className="border-2 border-slate-200 p-6 sticky top-32">
                        <div className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">Ready to apply?</div>
                        <div className="mt-2 font-heading text-2xl font-black uppercase text-brand-navy">One application. Six steps.</div>
                        <p className="mt-3 text-slate-600 text-sm">A specialist will contact you within 1–2 business days.</p>
                        <Link
                            to={`/apply?job=${job.id}`}
                            className="mt-6 block text-center bg-brand-orange hover:bg-brand-navy text-white font-heading uppercase tracking-wider font-bold px-5 py-4 rounded-sm transition-colors"
                            data-testid="apply-for-job-btn"
                        >
                            Apply for this Job
                        </Link>
                    </div>
                </aside>
            </section>
        </PageShell>
    );
}
